import { codeMap, http } from './http';
import { RISK_GRADE } from './codes';
import { RiskGrade } from '../lib/risk';

/**
 * 리스크 평가 — 회차 · 평가 · 답 · 변경 이력은 공통 REST 로 읽고,
 * 저장 · 제출 · 승인 · 반려는 전용 엔드포인트로 한다(상태 전이를 서버가 막는다).
 */
export const ASSESS_STATUS = codeMap({ not_started: '평가 전', draft: '작성 중', submitted: '제출', approved: '승인', rejected: '반려' } as const);
export type AssessStatus = '평가 전' | '작성 중' | '제출' | '승인' | '반려';

export interface RiskQuestion { id: number; code: string; area: string; text: string; kind: 'yn' | 'pick'; options: string[]; riskIf: string[] }
export interface RiskRound { id: number; code: string; startOn: string; dueOn: string | null; status: 'open' | 'closed' }
export interface RiskAssessment {
  id: number; roundId: number; dealId: number;
  status: AssessStatus; grade: RiskGrade | null; opinion: string; rejectReason: string | null;
  submittedAt: string | null; approvedAt: string | null;
  /** 문항 id → 답 */
  answers: Record<number, string>;
}
export interface AuditLog { id: number; entity: string; entityId: number; action: string; actorName: string; summary: string; createdAt: string }

const toUi = (r: any, answers: Record<number, string> = {}): RiskAssessment => ({
  id: r.id, roundId: r.roundId, dealId: r.dealId, status: ASSESS_STATUS.toUi(r.status)!, grade: RISK_GRADE.toUi(r.grade) as RiskGrade | null,
  opinion: r.opinion ?? '', rejectReason: r.rejectReason, submittedAt: r.submittedAt, approvedAt: r.approvedAt, answers,
});

export const riskApi = {
  async questions(): Promise<RiskQuestion[]> {
    const rows = await http.get<any[]>('/risk-questions', { isActive: 'true' });
    return rows.map((q) => ({ id: q.id, code: q.code, area: q.area, text: q.text, kind: q.answerType, options: q.options, riskIf: q.riskIf }));
  },
  async rounds(): Promise<RiskRound[]> {
    return (await http.get<RiskRound[]>('/risk-rounds')).sort((a, b) => b.code.localeCompare(a.code));
  },
  /** 모든 회차의 평가 + 답 — 추이 · 전 분기 비교에 다 필요하다 */
  async assessments(): Promise<RiskAssessment[]> {
    const rows = await http.get<any[]>('/risk-assessments');
    if (!rows.length) return [];
    const ans = await http.get<{ assessmentId: number; questionId: number; answer: string }[]>('/risk-answers', { assessmentId: rows.map((r) => r.id).join(',') });
    const by: Record<number, Record<number, string>> = {};
    ans.forEach((a) => { (by[a.assessmentId] ??= {})[a.questionId] = a.answer; });
    return rows.map((r) => toUi(r, by[r.id]));
  },
  async logs(): Promise<AuditLog[]> {
    return (await http.get<AuditLog[]>('/audit-logs', { entity: 'risk_rounds,risk_assessments' })).sort((a, b) => b.id - a.id);
  },
  /** 건별 최신 승인 등급 */
  async latestGrades(): Promise<Record<number, RiskGrade>> {
    const raw = await http.get<Record<string, string>>('/risk/latest-grades');
    return Object.fromEntries(Object.entries(raw).map(([k, g]) => [Number(k), RISK_GRADE.toUi(g) as RiskGrade]));
  },
  createRound: (code: string, dueOn?: string) => http.post<RiskRound & { targets: number }>('/risk-rounds/create', { code, dueOn: dueOn || null }),
  save: (a: RiskAssessment) => http.post(`/risk-assessments/${a.id}/save`, { answers: a.answers, grade: RISK_GRADE.toDb(a.grade), opinion: a.opinion }),
  submit: (id: number) => http.post(`/risk-assessments/${id}/submit`, {}),
  approve: (id: number) => http.post(`/risk-assessments/${id}/approve`, {}),
  reject: (id: number, reason: string) => http.post(`/risk-assessments/${id}/reject`, { reason }),
};
